import React, { ReactNode, useEffect, useMemo } from "react";

import {
    asBoolean,
    useClearFieldError,
    useFieldError,
    useRegisterFieldValidation,
} from "../core/formValidation";
import { createCheckboxValidator } from "../validation/defaultValidators";

export interface CheckboxProps
    extends Omit<
        React.InputHTMLAttributes<HTMLInputElement>,
        "type" | "name"
    > {
    name?: string;
    label?: string;
    children?: ReactNode;
    validate?: (value: boolean) => boolean;
    validationMessage?: string;
}

export const Checkbox: React.FC<CheckboxProps> = ({
    name,
    label,
    children,
    className,
    required = false,
    validate,
    validationMessage,
    ...inputProps
}) => {
    const registerFieldValidation = useRegisterFieldValidation();
    const clearFieldError = useClearFieldError();
    const fieldError = useFieldError(name);

    const effectiveValidator = useMemo(
        () => validate ?? createCheckboxValidator(required),
        [required, validate]
    );

    useEffect(() => {
        if (!name || !registerFieldValidation) {
            return;
        }

        return registerFieldValidation({
            name,
            message: validationMessage,
            validate: (value) => effectiveValidator(asBoolean(value)),
        });
    }, [effectiveValidator, name, registerFieldValidation, validationMessage]);

    const handleChange: React.ChangeEventHandler<HTMLInputElement> = (event) => {
        if (name) {
            clearFieldError(name);
        }

        inputProps.onChange?.(event);
    };

    return (
        <span className="wpcf7-form-control-wrap" data-name={name}>
            <span className="wpcf7-form-control wpcf7-acceptance wpcf7-optional">
                <span className="wpcf7-list-item">
                    <label className="checkbox-option">
                        <input
                            {...inputProps}
                            id={name}
                            name={name}
                            type="checkbox"
                            required={required}
                            onChange={handleChange}
                            className={[
                                fieldError ? "wpcf7-not-valid" : null,
                                className,
                            ]
                                .filter(Boolean)
                                .join(" ")}
                        />

                        <span className="wpcf7-list-item-label">{children ?? label}</span>
                    </label>
                </span>
            </span>

            {fieldError ? (
                <span className="wpcf7-not-valid-tip" aria-hidden="true">
                    {fieldError}
                </span>
            ) : null}
        </span>
    );
};

export const DSGVOCheckbox: React.FC = () => {
    return (
        <Checkbox name="dsgvo" required>
            Ja, ich habe die{" "}
            <a
                href="/datenschutzerklaerung"
                target="_blank"
                rel="noopener noreferrer"
            >
                Datenschutzerklaerung
            </a>{" "}
            zur Kenntnis genommen.
        </Checkbox>
    );
};

